import {
	CheckBox as CheckBoxIcon,
	CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
	Save as SaveIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid2,
	Paper,
	TextField,
	Typography
} from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { AGDialog } from "../../components/AGDialog";
import {
	useCreateRoleHierarchyMutation,
	useEditRoleHierarchyMutation,
	useGetUserRolesQuery
} from "../../redux/reducers/rolesPermissions.reducer";
import { IUserRoleHierarchyRowDetails } from "./RolesHierarchy";
import { useAppSelector } from "../../redux";

interface IHierarchyDetailsDialogProps {
	open: boolean;
	hierarchyDetails: IUserRoleHierarchyRowDetails | null;
	onClose: () => void;
}

interface IHierarchyInputs {
	role_name: string;
	child_roles: number[];
}

const HierarchyDetailsDialog: FC<IHierarchyDetailsDialogProps> = (props) => {
	const { open, hierarchyDetails, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	const readOnlyPermission = useMemo<boolean>(
		() => !authState.user.permissions || !authState.user.permissions.user_management?.edit,
		[authState.user.permissions]
	);

	const {
		reset,
		control,
		register,
		handleSubmit,
		formState: { isDirty: inputChanged }
	} = useForm<IHierarchyInputs>();

	// APIS
	// GET USER ROLES
	const {
		data: getUserRolesResponse,
		isLoading: getUserRolesLoading,
		isFetching: getUserRolesFetching
	} = useGetUserRolesQuery({});

	// CREATE ROLE HIERARCHY
	const [
		createRoleHierarchy,
		{ isLoading: createRoleHierarchyLoading, error: createRoleHierarchyError, reset: resetCreateRoleHierarchyState }
	] = useCreateRoleHierarchyMutation();

	// EDIT ROLE HIERARCHY
	const [
		editRoleHierarchy,
		{ isLoading: editRoleHierarchyLoading, error: editRoleHierarchyError, reset: resetEditRoleHierarchyState }
	] = useEditRoleHierarchyMutation();

	function handleSaveButtonClick(formData: IHierarchyInputs) {
		if (!hierarchyDetails) return;

		if (hierarchyDetails.hierarchy_id > 0) {
			editRoleHierarchy({
				id: hierarchyDetails.hierarchy_id,
				data: {
					group: hierarchyDetails.id,
					children: formData.child_roles ?? []
				}
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		} else {
			createRoleHierarchy({
				group: hierarchyDetails.id,
				children: formData.child_roles ?? []
			})
				.unwrap()
				.then(() => onClose())
				.catch(() => {});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				role_name: hierarchyDetails?.role_name ?? "",
				child_roles: hierarchyDetails?.children?.map((item) => item.id) ?? []
			});
		} else {
			resetCreateRoleHierarchyState();
			resetEditRoleHierarchyState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hierarchyDetails?.children, open]);

	return (
		<AGDialog open={open} onClose={onClose} title="User Role Hierarchy" color="primary" maxWidth="xs">
			<Box>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="Name"
							autoComplete="off"
							slotProps={{ input: { readOnly: true } }}
							{...register("role_name")}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Controller
							control={control}
							name="child_roles"
							render={({ field: { value, onChange } }) => (
								<>
									<Autocomplete
										fullWidth
										multiple
										disableCloseOnSelect
										limitTags={1}
										readOnly={readOnlyPermission}
										loading={getUserRolesLoading || getUserRolesFetching}
										options={getUserRolesResponse ?? []}
										getOptionLabel={(option) => option.name}
										getOptionKey={(option) => option.id}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										value={getUserRolesResponse?.filter((item) => value?.includes(item.id)) ?? []}
										onChange={(_event, newValue) => onChange(newValue?.map((item) => item.id) ?? [])}
										renderTags={() => <></>}
										renderInput={(params) => <TextField {...params} fullWidth size="small" label="Lower-level Roles" />}
										renderOption={({ key, ...props }, option, { selected }) => (
											<li key={key} {...props} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
												{selected ? (
													<CheckBoxIcon color="primary" sx={{ marginY: "0.25rem" }} />
												) : (
													<CheckBoxOutlineBlankIcon color="primary" sx={{ marginY: "0.25rem" }} />
												)}

												<Typography>{option.name}</Typography>
											</li>
										)}
										PaperComponent={(props) => {
											const { children, ...restProps } = props;
											const AllRolesSelected =
												value.length === 0 ? "none" : value.length === getUserRolesResponse?.length ? "all" : "partial";

											return (
												<Paper {...restProps}>
													<Box
														onMouseDown={(event) => event.preventDefault()}
														sx={{ paddingX: "1.25rem", paddingY: "0.25rem", width: "100%" }}
													>
														<FormControlLabel
															label="Select All"
															sx={{ width: "100%" }}
															control={
																<Checkbox
																	checked={AllRolesSelected === "all"}
																	indeterminate={AllRolesSelected === "partial"}
																/>
															}
															onClick={(event) => {
																event.preventDefault();
																if (AllRolesSelected === "none" || AllRolesSelected === "partial") {
																	onChange(getUserRolesResponse?.map((item) => item.id) ?? []);
																} else if (AllRolesSelected === "all") {
																	onChange([]);
																}
															}}
														/>
													</Box>

													<Divider />

													{children}

													<Divider />

													<Box
														sx={{
															padding: "0.5rem",
															width: "100%",
															display: "flex",
															justifyContent: "flex-end"
														}}
													>
														<Button>Done</Button>
													</Box>
												</Paper>
											);
										}}
									/>

									<Typography sx={{ marginTop: value.length === 0 ? "0.5rem" : "0.5rem" }}>
										{value.length === getUserRolesResponse?.length
											? "All user roles selected"
											: value.length === 0
											? "No user role selected"
											: `${value.length} user role${value.length !== 1 ? "s" : ""} selected`}
									</Typography>
								</>
							)}
						/>
					</Grid2>

					{createRoleHierarchyError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
								{"data" in createRoleHierarchyError && createRoleHierarchyError.data
									? String(createRoleHierarchyError.data)
									: "Something went wrong"}
							</Typography>
						</Grid2>
					) : null}

					{editRoleHierarchyError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
								{"data" in editRoleHierarchyError && editRoleHierarchyError.data
									? String(editRoleHierarchyError.data)
									: "Something went wrong"}
							</Typography>
						</Grid2>
					) : null}

					{!readOnlyPermission ? (
						<Grid2 size={{ xs: 12 }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
								<Button variant="outlined" onClick={onClose}>
									Cancel
								</Button>

								<LoadingButton
									disableElevation
									variant="contained"
									color="success"
									startIcon={<SaveIcon />}
									onClick={handleSubmit(handleSaveButtonClick)}
									loading={createRoleHierarchyLoading || editRoleHierarchyLoading}
									loadingPosition="start"
									disabled={!inputChanged}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid2>
					) : null}
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default HierarchyDetailsDialog;
